import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import {
  IFoodItem,
  IMasterFoodMenu,
  IFoodMenuCategory,
  ILeadFoodMenu,
} from "./food.types";

export const FETCH_FOOD_ITEMS_LIST_PROGRESS = "FETCH_FOOD_ITEMS_LIST_PROGRESS";
export const FETCH_FOOD_ITEMS_LIST_SUCCESS = "FETCH_FOOD_ITEMS_LIST_SUCCESS";
export const FETCH_FOOD_ITEMS_LIST_FAILED = "FETCH_FOOD_ITEMS_LIST_FAILED";

export const fetchFoodItemsListProgress = () =>
  action(FETCH_FOOD_ITEMS_LIST_PROGRESS);
export const fetchFoodItemsListSuccess = (
  data: IFoodItem[],
  totalRecords: number
) => action(FETCH_FOOD_ITEMS_LIST_SUCCESS, { data, totalRecords });
export const fetchFoodItemsListFailed = () =>
  action(FETCH_FOOD_ITEMS_LIST_FAILED);

export const fetchFoodItemsListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchFoodItemsListProgress());
    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (searchByName) {
        searchQuery += `&item_name=${searchByName}`;
      }

      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const response = await api.get(
        `/food/get-food-item?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery
      );
      const data: IFoodItem[] = response.data.data;

      const totalRecords = response.data.totalRecords;
      dispatch(fetchFoodItemsListSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchFoodItemsListFailed());
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertFoodItem =
  (
    data: IFoodItem,
    onCallback?: (isSuccess: boolean, data?: IFoodItem) => void
  ) =>
  async (dispatch: any) => {
    try {
      const response = await api.post("/food/upsert-food-item", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: "Food item created successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data);
      return response.data;
    } catch (error: any) {
      onCallback?.(false);
      dispatch(
        showMessage({
          type: "error",
          message: error.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

//food menus actions
export const FETCH_FOOD_MENUS_LIST_PROGRESS = "FETCH_FOOD_MENUS_LIST_PROGRESS";
export const FETCH_FOOD_MENUS_LIST_SUCCESS = "FETCH_FOOD_MENUS_LIST_SUCCESS";
export const FETCH_FOOD_MENUS_LIST_FAILED = "FETCH_FOOD_MENUS_LIST_FAILED";

export const fetchFoodMenusListProgress = () =>
  action(FETCH_FOOD_MENUS_LIST_PROGRESS);
export const fetchFoodMenusListSuccess = (
  data: IMasterFoodMenu[],
  totalRecords: number
) => action(FETCH_FOOD_MENUS_LIST_SUCCESS, { data, totalRecords });
export const fetchFoodMenusListFailed = () =>
  action(FETCH_FOOD_MENUS_LIST_FAILED);

export const fetchMasterFoodMenusListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    masterMenuUuid?: string,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchFoodMenusListProgress());

    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (searchByName) {
        searchQuery += `&title=${searchByName}`;
      }
      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }
      searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }
      if (masterMenuUuid) {
        searchQuery += `&master_menu_uuid=${masterMenuUuid}`;
      }

      const response = await api.get(
        `/food/get-master-menu?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery
      );
      const data: IMasterFoodMenu[] = response.data.data;

      const totalRecords = response.data.totalRecords;
      dispatch(fetchFoodMenusListSuccess(data, totalRecords));
    } catch (error) {
      dispatch(fetchFoodMenusListFailed());
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertMasterFoodMenu =
  (data: IMasterFoodMenu, onCallback?: (isSuccess: boolean, data?: IMasterFoodMenu) => void) => async (dispatch: any) => {
    try {
      const response = await api.post("/food/upsert-master-menu", data);

      dispatch(
        showMessage({
          type: "success",
          message: `${
            data.master_menu_uuid
              ? "Master Food menu updated successfully"
              : "Master Food menu created successfully"
          }`,
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data);
      return response.data;
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          message: error.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

// Lead  menu actions

export const FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS =
  "FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS";
export const FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS =
  "FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS";
export const FETCH_LEAD_FOOD_MENUS_LIST_FAILED =
  "FETCH_LEAD_FOOD_MENUS_LIST_FAILED";

export const fetchLeadFoodMenusListProgress = () =>
  action(FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS);
export const fetchLeadFoodMenusListSuccess = (
  data: ILeadFoodMenu[],
  totalRecords: number
) => action(FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS, { data, totalRecords });
export const fetchLeadFoodMenusListFailed = () =>
  action(FETCH_LEAD_FOOD_MENUS_LIST_FAILED);

export const fetchLeadFoodMenusListAsync =
  (
    leads_uuid: string,
    company_outlet_uuid: string,
    added_lead_menu_uuid?: string | null,
    pageNo?: number,
    itemPerPage?: number,
    pageLimit?: number,

    advanceFilter?: IDatatableV2AdvancedSearchFilter,
    onCallback?: (isSuccess: boolean, data?: ILeadFoodMenu[]) => void
  ) =>
  async (dispatch: any) => {
    dispatch(fetchLeadFoodMenusListProgress());
    try {
      let searchQuery = "";

      if (added_lead_menu_uuid) {
        searchQuery += `&added_lead_menu_uuid=${added_lead_menu_uuid}`;
      }

      if (pageNo) {
        searchQuery += `&pageNo=${pageNo}`;
      }

      if (itemPerPage) {
        searchQuery += `&itemPerPage=${itemPerPage}`;
      }
      if (pageLimit) {
        searchQuery += `&pageLimit=${pageLimit}`;
      }

      const response = await api.get(
        `/food/get-lead-menu?leads_uuid=${leads_uuid}&company_outlet_uuid=${company_outlet_uuid}` +
          searchQuery
      );
      const data: ILeadFoodMenu[] = response.data.data;
      const totalRecords = response.data.totalRecords;
      dispatch(fetchLeadFoodMenusListSuccess(data, totalRecords));
      onCallback?.(true, data);
    } catch (error) {
      onCallback?.(false);
      dispatch(fetchLeadFoodMenusListFailed());
    }
  };

export const upsertLeadFoodMenu =
  (
    data: ILeadFoodMenu,
    onCallback?: (isSuccess: boolean, data?: ILeadFoodMenu) => void
  ) =>
  async (dispatch: any) => {
    try {
      const response = await api.post("/food/upsert-lead-menu", data);

      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: `${
            data.added_lead_menu_uuid === null
              ? "Lead Food menu Created successfully"
              : "Lead Food menu Saved successfully"
          }`,
          displayAs: "snackbar",
        })
      );

      // return response.data;
      onCallback?.(true, response.data);
    } catch (error: any) {
      onCallback?.(false);
      dispatch(fetchLeadFoodMenusListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: error.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// categories actions

export const FETCH_CATEGORY_LIST_PROGRESS = "FETCH_CATEGORY_LIST_PROGRESS";
export const FETCH_CATEGORY_LIST_SUCCESS = "FETCH_CATEGORY_LIST_SUCCESS";
export const FETCH_CATEGORY_LIST_FAILED = "FETCH_CATEGORY_LIST_FAILED";

export const fetchCategoryListProgress = () =>
  action(FETCH_CATEGORY_LIST_PROGRESS);
export const fetchCategoryListSuccess = (
  data: IFoodMenuCategory[],
  currentRecords: number
) => action(FETCH_CATEGORY_LIST_SUCCESS, { data, currentRecords });
export const fetchCategoryListFailed = () => action(FETCH_CATEGORY_LIST_FAILED);

export const fetchFoodMenuCategoryListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    category_uuid?: string | null,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchCategoryListProgress());
    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if(searchByName) {
        searchQuery += `&category_name=${searchByName}`;
      }
      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }
      searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;

      if (category_uuid) {
        searchQuery += `&category_uuid=${category_uuid}`;
      }

      if (status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const response = await api.get(
        `/food/get-category?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery
      );

      dispatch(
        fetchCategoryListSuccess(
          response.data.data,
          response.data.currentRecords
        )
      );
    } catch (error) {
      dispatch(fetchCategoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to fetch categories",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertFoodMenuCategory =
  (
    data: Partial<IFoodMenuCategory>,
    onCallback?: (isSuccess: boolean, data?: IFoodMenuCategory) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.post("/food/upsert-category", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: "Category saved successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data);
      return response.data;
    } catch (error: any) {
      onCallback?.(false);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message: error.response?.data?.message || "Failed to save category",
          displayAs: "snackbar",
        })
      );
      throw error;
    }
  };

export const CLEAR_FOOD_STATE = "CLEAR_FOOD_STATE";
export const clearFoodState = () => action(CLEAR_FOOD_STATE);
